/* eslint-disable react-hooks-ext/exhaustive-deps -- TODO(react-hooks): 2 hook(s) need dependency review; fix each and remove this directive */
import React, { forwardRef, useImperativeHandle, useState, useEffect } from "react";
import { LinearProgress, CircularProgress, Box, Typography, alpha, linearProgressClasses } from "@mui/material";
import type { SxProps, Theme } from "@mui/material";
import { cssLengthToPx } from "@CubeComponents/utils/cssLengthToPx";

export type LoaderVariant = "small" | "large";

export interface ProgressBarLoadArgs {
  progressBar: React.ForwardedRef<ProgressBarComponentRef>;
}

export interface ProgressBarComponentProps {
  id?: string;
  value?: number;
  minimum?: number;
  maximum?: number;
  type?: "Linear" | "Circular";
  height?: string | number;
  width?: string | number;
  trackColor?: string;
  progressColor?: string;
  trackThickness?: number;
  progressThickness?: number;
  cornerRadius?: "Square" | "Round";
  isIndeterminate?: boolean;
  showProgressValue?: boolean;
  variant?: LoaderVariant;
  disabled?: boolean;
  // Annotation content - for circular type
  annotationContent?: string | React.ReactNode;
  animation?: {
    enable?: boolean;
    duration?: number;
    delay?: number;
  };
  labelStyle?: React.CSSProperties;
  // Callbacks - Syncfusion compatible
  progressCompleted?: (args: { value: number }) => void;
  valueChanged?: (args: { value: number }) => void;
  load?: (args: ProgressBarLoadArgs) => void;
  // Style props
  className?: string;
  cssClass?: string;
  style?: React.CSSProperties;
  sx?: SxProps<Theme>;
  children?: React.ReactNode;
}

export interface ProgressBarComponentRef {
  value: number;
  refresh: () => void;
}

// Re-export for compatibility with Syncfusion pattern
export const ProgressBarAnnotationDirective = ({ content: _content }: { content: string | React.ReactNode }) => null;
export const ProgressBarAnnotationsDirective = ({ children: _children }: { children: React.ReactNode }) => null;
export const ProgressAnnotation = {};
export const Inject = ({ services: _services }: { services: unknown[] }) => null;

const LOADER_VARIANTS = {
  small: {
    barHeight: 4,
    boxPadding: "0.5rem",
    fontSize: "0.75rem",
    lineHeight: "120%",
  },
  large: {
    barHeight: 6,
    boxPadding: "1rem",
    fontSize: "1rem",
    lineHeight: "120%",
  },
};

const LOADER_COLORS = {
  enabled: {
    primary: "#6BBBE3",
    trackOpacity: 0.25,
    text: "#666A84",
  },
  disabled: {
    primary: "#666A84",
    trackOpacity: 0.5,
    text: "#666A84",
  },
};

const ProgressBarComponent = forwardRef<ProgressBarComponentRef, ProgressBarComponentProps>((props, ref) => {
  const {
    id,
    value: propValue = 0,
    minimum = 0,
    maximum = 100,
    type = "Linear",
    height,
    width,
    trackColor,
    progressColor,
    trackThickness = 4,
    progressThickness = 4,
    cornerRadius = "Round",
    isIndeterminate = false,
    showProgressValue = false,
    variant = "large",
    disabled = false,
    annotationContent,
    animation,
    labelStyle,
    progressCompleted,
    valueChanged,
    load,
    className,
    cssClass,
    style,
    sx,
    children,
  } = props;

  const loaderConfig = LOADER_VARIANTS[variant];
  const loaderColors = disabled ? LOADER_COLORS.disabled : LOADER_COLORS.enabled;

  // Resolve defaults based on type
  const resolvedHeight = height ?? (type === "Circular" ? 120 : loaderConfig.barHeight);
  const resolvedWidth = width ?? (type === "Circular" ? "7.5rem" : "100%");
  const resolvedTrackColor = trackColor ?? alpha(loaderColors.primary, loaderColors.trackOpacity);

  const [value, setValue] = useState(propValue);

  useEffect(() => {
    if (load) {
      load({ progressBar: ref });
    }
  }, []);

  useEffect(() => {
    setValue(propValue);
    if (valueChanged) {
      valueChanged({ value: propValue });
    }
    if (propValue >= maximum && progressCompleted) {
      progressCompleted({ value: propValue });
    }
  }, [propValue, maximum]);

  useImperativeHandle(ref, () => ({
    get value() {
      return value;
    },
    set value(val: number) {
      setValue(val);
    },
    refresh: () => {
      setValue((prev) => prev);
    },
  }));

  // Normalize value to 0-100 range
  const normalizedValue = Math.min(100, Math.max(0, ((value - minimum) / (maximum - minimum)) * 100));

  // Handle gradient colors
  const getProgressColor = () => {
    if (progressColor?.startsWith("url(")) {
      return undefined; // Will use gradient
    }
    return progressColor || loaderColors.primary;
  };

  const useGradient = progressColor?.startsWith("url(");
  const gradientId = `progressGradient_${id || Math.random().toString(36).substr(2, 9)}`;

  // Extract annotation content from children if using Syncfusion-style directives
  let annotationFromChildren: string | React.ReactNode = annotationContent;
  if (children) {
    // `isValidElement<P>` is React's own way to state the props shape a directive element carries;
    // its default leaves `props` as `unknown`.
    React.Children.forEach(children, (child) => {
      if (!React.isValidElement<{ children?: React.ReactNode }>(child)) return;
      if (child.type === ProgressBarAnnotationsDirective || child.props.children != null) {
        React.Children.forEach(child.props.children, (annotation) => {
          if (!React.isValidElement<{ content?: React.ReactNode }>(annotation)) return;
          if (annotation.props.content != null) {
            annotationFromChildren = annotation.props.content;
          }
        });
      }
    });
  }

  if (type === "Circular") {
    const numericSize = cssLengthToPx(resolvedHeight as string | number);

    return (
      <Box
        id={id}
        className={className || cssClass}
        style={style}
        sx={[
          {
            position: "relative",
            display: "inline-flex",
            width: numericSize,
            height: numericSize,
            opacity: disabled ? 0.5 : 1,
          },
          ...(Array.isArray(sx) ? sx : sx != null ? [sx] : []),
        ]}
      >
        {/* SVG Gradient Definition */}
        {useGradient && (
          <svg width={0} height={0}>
            <defs>
              <linearGradient id={gradientId} x1="0%" y1="0%" x2="100%" y2="0%">
                <stop offset="0%" style={{ stopColor: "#6BBBE3" }} />
                <stop offset="100%" style={{ stopColor: "#3E8AB0" }} />
              </linearGradient>
            </defs>
          </svg>
        )}
        {/* Also render any SVG children (for custom gradients) */}
        {children}
        {/* Track (background circle) */}
        <CircularProgress
          variant="determinate"
          value={100}
          size={numericSize}
          thickness={trackThickness}
          sx={{
            position: "absolute",
            color: resolvedTrackColor,
            "& .MuiCircularProgress-circle": {
              strokeLinecap: cornerRadius === "Round" ? "round" : "butt",
            },
          }}
        />
        {/* Progress circle */}
        <CircularProgress
          variant={isIndeterminate ? "indeterminate" : "determinate"}
          value={normalizedValue}
          size={numericSize}
          thickness={progressThickness}
          sx={{
            position: "absolute",
            color: getProgressColor(),
            "& .MuiCircularProgress-circle": {
              strokeLinecap: cornerRadius === "Round" ? "round" : "butt",
              stroke: useGradient ? `url(#${gradientId})` : undefined,
              transition:
                animation?.enable !== false ? `stroke-dashoffset ${animation?.duration || 300}ms ease` : "none",
            },
          }}
        />
        {/* Center content */}
        <Box
          sx={{
            position: "absolute",
            top: 0,
            insetInlineStart: 0,
            bottom: 0,
            insetInlineEnd: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          {annotationFromChildren ? (
            typeof annotationFromChildren === "string" ? (
              <div dangerouslySetInnerHTML={{ __html: annotationFromChildren }} />
            ) : (
              annotationFromChildren
            )
          ) : showProgressValue ? (
            <Typography
              variant="caption"
              component="div"
              sx={{
                color: "text.secondary",
              }}
            >
              {`${Math.round(normalizedValue)}%`}
            </Typography>
          ) : null}
        </Box>
      </Box>
    );
  }

  // Linear progress bar
  return (
    <Box
      id={id}
      className={className || cssClass}
      style={{ width: resolvedWidth, ...style }}
      sx={[
        {
          display: "flex",
          padding: loaderConfig.boxPadding,
          alignItems: "center",
          gap: "1rem",
          boxSizing: "border-box",
          opacity: disabled ? 0.5 : 1,
        },
        ...(Array.isArray(sx) ? sx : sx != null ? [sx] : []),
      ]}
    >
      {/* SVG Gradient Definition for url() references */}
      {useGradient && (
        <svg width={0} height={0}>
          <defs>
            <linearGradient id={gradientId} x1="0%" y1="0%" x2="100%" y2="0%">
              <stop offset="0%" style={{ stopColor: "#6BBBE3" }} />
              <stop offset="100%" style={{ stopColor: "#3E8AB0" }} />
            </linearGradient>
          </defs>
        </svg>
      )}

      {children}

      <Box sx={{ width: "100%", me: 1 }}>
        <LinearProgress
          variant={isIndeterminate ? "indeterminate" : "determinate"}
          value={normalizedValue}
          sx={{
            height: (() => {
              if (typeof resolvedHeight === "number") return resolvedHeight;
              const px = cssLengthToPx(resolvedHeight as string);
              return Number.isFinite(px) && px > 0 ? px : loaderConfig.barHeight;
            })(),
            borderRadius: "6.25rem",
            backgroundColor: resolvedTrackColor,
            [`& .${linearProgressClasses.bar}`]: {
              borderRadius: "6.25rem",
              backgroundColor: useGradient ? undefined : getProgressColor(),
              background: useGradient ? `linear-gradient(90deg, #6BBBE3, #3E8AB0)` : undefined,
              transition: animation?.enable !== false ? `transform ${animation?.duration || 400}ms ease` : "none",
            },
          }}
        />
      </Box>
      {showProgressValue && (
        <Box sx={{ minWidth: "2.1875rem" }}>
          <Typography
            variant="body2"
            style={labelStyle}
            sx={{
              color: loaderColors.text,
              textAlign: "center",
              fontSize: loaderConfig.fontSize,
              fontWeight: 600,
              lineHeight: loaderConfig.lineHeight,
            }}
          >
            {`${Math.round(normalizedValue)}%`}
          </Typography>
        </Box>
      )}
    </Box>
  );
});

ProgressBarComponent.displayName = "ProgressBarComponent";

export default ProgressBarComponent;
